import { ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { Position } from 'types'
import DraggableContext, { DraggableContextProps } from 'ui/src/components/draggable/context'
import { Container, Dim, Dimensions, Draggable } from './style'

type Props = {
    aspectRatio?: number
    children?: ReactNode
    className?: string
    crop?: Position
    defaultCrop?: Position
    draggableOptions?: Partial<DraggableContextProps>
    height: number
    showDimensions?: boolean
    width: number
    onChange?: (crop: Position) => void
}

const CropWithoutContext = ({
    aspectRatio,
    children,
    className,
    crop: cropProp,
    defaultCrop: defaultCropProp,
    height,
    showDimensions,
    width,
    onChange,
}: Props) => {
    const defaultCrop = useMemo(() => defaultCropProp || { x: 0, y: 0, width, height }, [])
    const initialValueRef = useRef<Position | undefined>(cropProp || defaultCrop)
    const [crop, setCrop] = useState<Position>(cropProp || defaultCrop)
    const [drag, setDrag] = useState<Position>(crop)
    const bounds = useMemo(() => ({ bottom: height, left: 0, right: width, top: 0 }), [height, width])

    useEffect(() => {
        if (!cropProp) return

        const keys = Object.keys(crop)
        const hasChanged = !!keys.find((key) => (crop as any)[key] !== (cropProp as any)[key])

        if (hasChanged) {
            setCrop(cropProp)
        }
    }, [cropProp?.height, cropProp?.rotation, cropProp?.width, cropProp?.x, cropProp?.y])

    useEffect(() => {
        const keys = Object.keys(crop)
        const hasChanged = !!keys.find((key) => {
            if (!initialValueRef.current) {
                return true
            }
            return (crop as any)[key] !== (initialValueRef.current as any)[key]
        })

        if (hasChanged) {
            initialValueRef.current = undefined
            onChange?.(crop)
        }
    }, [crop.height, crop.rotation, crop.width, crop.x, crop.y])

    return (
        <Container className={className} style={{ height, width }}>
            {children}
            <Dim bounds={bounds} crop={drag} />
            <Draggable aspectRatio={aspectRatio} bounds={bounds} crop={crop} onDrag={setDrag} onDragEnd={setCrop}>
                {showDimensions && (
                    <Dimensions>
                        {drag.width} x {drag.height}
                    </Dimensions>
                )}
            </Draggable>
        </Container>
    )
}

const Crop = ({ draggableOptions = {}, ...props }: Props) => (
    <DraggableContext {...draggableOptions}>
        <CropWithoutContext {...props} />
    </DraggableContext>
)

export default Crop
